import { Component } from '@theme/component';
import { isClickedOutside } from '@theme/utilities';

/**
 * A custom element that displays a header dropdown: a trigger button and a panel holding
 * the block's nested theme blocks.
 *
 * @typedef {object} HeaderDropdownRefs
 * @property {HTMLButtonElement} button - The trigger button.
 * @property {HTMLElement} panel - The panel revealed when the dropdown opens.
 *
 * @extends {Component<HeaderDropdownRefs>}
 */
class HeaderDropdownComponent extends Component {
  requiredRefs = ['button', 'panel'];

  connectedCallback() {
    super.connectedCallback();

    if (!window.Shopify?.designMode) return;

    // The panel is closed by default, so selecting a nested block in the editor would
    // otherwise scroll to something the merchant cannot see.
    this.addEventListener('shopify:block:select', this.open);
    this.addEventListener('shopify:block:deselect', this.close);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('shopify:block:select', this.open);
    this.removeEventListener('shopify:block:deselect', this.close);
    this.#stopListening();
  }

  /**
   * Whether the panel is currently open.
   *
   * @returns {boolean} True when the panel is visible.
   */
  get isOpen() {
    return !this.refs.panel.hasAttribute('hidden');
  }

  /**
   * Toggles the panel.
   */
  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Opens the panel.
   */
  open = () => {
    if (this.isOpen) return;

    document.addEventListener('click', this.#handleClickOutside);
    this.addEventListener('keyup', this.#handleKeyUp);

    this.refs.panel.removeAttribute('hidden');
    this.refs.button.setAttribute('aria-expanded', 'true');
  };

  /**
   * Closes the panel.
   */
  close = () => {
    if (!this.isOpen) return;

    this.#stopListening();

    this.refs.panel.setAttribute('hidden', '');
    this.refs.button.setAttribute('aria-expanded', 'false');
  };

  #stopListening() {
    document.removeEventListener('click', this.#handleClickOutside);
    this.removeEventListener('keyup', this.#handleKeyUp);
  }

  /**
   * Closes the panel when the click landed outside of it.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleClickOutside = (event) => {
    if (isClickedOutside(event, this)) this.close();
  };

  /**
   * Closes the panel on Escape and returns focus to the trigger.
   *
   * @param {KeyboardEvent} event - The keyup event.
   */
  #handleKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.close();
    event.stopPropagation();
    this.refs.button.focus();
  };
}

if (!customElements.get('header-dropdown-component')) {
  customElements.define('header-dropdown-component', HeaderDropdownComponent);
}
